import type { EntitiesState, EntityName } from "StoreTypes";

/**
 * A normalized entity map keyed by ID.
 * Mirrors the shape used throughout EntitiesState.
 */
type EntityMap<T> = Record<string, T>;

/**
 * An updater function that transforms an entity map.
 * This is the signature expected by redux-query's updateEntities.
 */
type EntityUpdater<T> = (prev: EntityMap<T>) => EntityMap<T>;

/**
 * Extracts the entity type from a given entity name in the store.
 */
type EntityType<K extends EntityName> =
  EntitiesState[K] extends EntityMap<infer T> ? T : never;

/**
 * Creates an updater function for a single entity in a normalized state map.
 * Returns the previous state unchanged if the entity doesn't exist (no-op).
 *
 * @param id - The entity's unique identifier
 * @param updater - Either a partial object to merge, or a function that receives
 *                  the existing entity and returns a partial update
 *
 * @example
 * // Simple partial merge
 * dispatch(updateEntities({
 *   simulatorStates: updateEntity(simulatorId, { timerStatus: TimerStatus.STOPPED }),
 * }));
 *
 * @example
 * // Computed update using existing values
 * dispatch(updateEntities({
 *   simulatorStates: updateEntity(simulatorId, (existing) => ({
 *     timerValue: subtractSecondsFromTimerArray(existing.timerValue, 1),
 *   })),
 * }));
 */
export function updateEntity<T>(
  id: string | number | undefined,
  updater: Partial<T> | ((existing: T) => Partial<T>),
): EntityUpdater<T> {
  if (!id) return (prev) => prev;
  const key = String(id);

  return (prev) => {
    const existing = prev[key];
    if (!existing) return prev;

    const changes = typeof updater === "function" ? updater(existing) : updater;

    return {
      ...prev,
      [key]: { ...existing, ...changes },
    };
  };
}

/**
 * Type-safe version of updateEntity that validates against a specific entity slice.
 * Provides better autocomplete and compile-time checks for entity properties.
 *
 * @param _entityName - The entity name (used only for type inference)
 * @param id - The entity's unique identifier
 * @param updater - Either a partial object to merge, or a function that receives
 *                  the existing entity and returns a partial update
 *
 * @example
 * dispatch(updateEntities({
 *   simulatorStates: updateEntityFor('simulatorStates', simulatorId, (existing) => ({
 *     timerStatus: TimerStatus.RUNNING,
 *     timerValue: addMinutesToTimerArray(existing.timerValue, time),
 *   })),
 * }));
 */
export function updateEntityFor<K extends EntityName>(
  _entityName: K,
  id: string | number,
  updater:
    | Partial<EntityType<K>>
    | ((existing: EntityType<K>) => Partial<EntityType<K>>),
): EntityUpdater<EntityType<K>> {
  return updateEntity<EntityType<K>>(id, updater);
}
